import { useEffect, useRef, useState } from 'react'
import RecursiveListFetcher from './components/recursive-list-fetcher.jsx'
import TextInputComponent from '../stories/molecules/text-input/index.jsx'
import { FormattedMessage, useIntl } from 'react-intl'
import ContextMenuComponent from '../stories/organisms/context-menu/index.jsx'
import {
  RecursiveListContainer,
  RecursiveItemIcon,
  RecursiveItemLabel,
  RecursiveItemOption,
  LoadContainer,
  IconListIsOpen,
  RecursiveLisTrigger
} from './styled-components.js'
import { iconSizeOptions } from '../stories/atoms/icons/icons-sizes.js'
import {
  Confirm,
  ChevronDownSmall
} from '../stories/atoms/icons/essentials/index.js'
import RecursiveListSearch from './components/recursive-list-search.jsx'
import { SpinnerGrey } from '../stories/atoms/icons/special/index.js'
import { Portal } from '../portal'
import { useGetProjectFolderDetailsQuery } from '../../react-query/project/query/use-get-project-folder-details-query.jsx'
import PropTypes from 'prop-types'
import { textInputHintVariantOptions } from '../stories/molecules/text-input/constants.js'
import { recursiveListTypeOptions } from './constants.js'
import { useGetPlanFolderDetailsQuery } from '../../react-query/plan/query/use-get-plan-folder-details-query.jsx'

const RecursiveFolderList = ({
  onSelectItem = () => {},
  textInputHint = null,
  defaultFolderId = null,
  listType = recursiveListTypeOptions.PROJECT,
  projectId = null
}) => {
  const [search, setSearch] = useState('')
  const [selectedItem, setSelectedItem] = useState(null)
  const [isFolderMenuOpen, setIsFolderMenuOpen] = useState(false)
  const { formatMessage } = useIntl()

  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    width: 0
  })
  const triggerRef = useRef(null)

  const handleListIcon = (
    <IconListIsOpen isOpen={isFolderMenuOpen}>
      <ChevronDownSmall size={iconSizeOptions['ICON-XS']} />
    </IconListIsOpen>
  )

  const { data: projectFolder, isLoading: projectLoading } =
    useGetProjectFolderDetailsQuery(defaultFolderId, [], {
      enabled:
        listType === recursiveListTypeOptions.PROJECT && !!defaultFolderId
    })

  const { data: planFolder, isLoading: planLoading } =
    useGetPlanFolderDetailsQuery(defaultFolderId, {
      enabled: listType === recursiveListTypeOptions.PLAN && !!defaultFolderId
    })

  const defaultFolderInfo =
    listType === recursiveListTypeOptions.PROJECT ? projectFolder : planFolder

  const isDefaultFolderLoading =
    listType === recursiveListTypeOptions.PROJECT ? projectLoading : planLoading

  const defineInputValue =
    selectedItem?.name ?? formatMessage({ id: 'main_folder' })

  const mainFolderSelected = selectedItem === null

  const handleSelectMainFolder = () => {
    setSelectedItem(null)
  }

  const handleOpenFolderList = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()

      setMenuPosition({
        top: rect.bottom + 8,
        width: rect.width + 22
      })
    }

    setIsFolderMenuOpen(!isFolderMenuOpen)
  }

  const handleOnMouseDown = (e) => {
    e.stopPropagation()
  }

  useEffect(() => {
    onSelectItem(selectedItem)
    setIsFolderMenuOpen(false)
  }, [selectedItem])

  useEffect(() => {
    if (defaultFolderInfo) setSelectedItem(defaultFolderInfo)
  }, [defaultFolderInfo])

  if (isDefaultFolderLoading) {
    return (
      <LoadContainer>
        <SpinnerGrey />
      </LoadContainer>
    )
  }

  return (
    <RecursiveListContainer>
      <ContextMenuComponent.Root
        isOpen={isFolderMenuOpen}
        setIsOpen={setIsFolderMenuOpen}
        style={{
          width: '100%'
        }}
      >
        <RecursiveLisTrigger onClick={handleOpenFolderList}>
          <TextInputComponent
            placeholder={formatMessage({ id: 'folder' })}
            locked
            value={defineInputValue}
            hintVariant={textInputHintVariantOptions.ERROR}
            hintShow={!!textInputHint}
            hintMessage={textInputHint}
            ref={triggerRef}
            rightIcon={handleListIcon}
          />
        </RecursiveLisTrigger>

        <Portal node={document.body}>
          <ContextMenuComponent.List
            hideScrollbar
            onMouseDown={handleOnMouseDown}
            style={{
              position: 'absolute',
              zIndex: 9999,
              ...menuPosition
            }}
          >
            <>
              {/* search */}
              <RecursiveListSearch
                setSearchValue={setSearch}
                searchValue={search}
              />
              <ContextMenuComponent.Divider />
              {/* main folder item */}
              <RecursiveItemOption
                onClick={handleSelectMainFolder}
                isSelected={mainFolderSelected}
              >
                <RecursiveItemLabel isSelected={mainFolderSelected}>
                  <FormattedMessage id="main_folder" />
                </RecursiveItemLabel>
                {mainFolderSelected && (
                  <RecursiveItemIcon>
                    <Confirm size={iconSizeOptions['ICON-S']} />
                  </RecursiveItemIcon>
                )}
              </RecursiveItemOption>
              {/* recursive folder list */}
              <RecursiveListFetcher
                projectId={projectId}
                search={search}
                selectedItem={selectedItem}
                setSelectedItem={setSelectedItem}
                listType={listType}
              />
            </>
          </ContextMenuComponent.List>
        </Portal>
      </ContextMenuComponent.Root>
    </RecursiveListContainer>
  )
}

RecursiveFolderList.propTypes = {
  onSelectItem: PropTypes.func,
  textInputHint: PropTypes.string,
  defaultFolderId: PropTypes.string,
  listType: PropTypes.oneOf(Object.values(recursiveListTypeOptions)),
  projectId: PropTypes.string
}

export default RecursiveFolderList
